import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { recruiterAPI } from '../services/api';
import RecruiterSidebar from '../components/RecruiterSidebar';
import { API, MEDIA_URL } from '../config';
import '../styles/theme.css';
import './RecruiterProfile.css';

const SA_PROVINCES = ['Gauteng','Western Cape','KwaZulu-Natal','Eastern Cape','Limpopo','Mpumalanga','North West','Free State','Northern Cape'];
const COMPANY_SIZES = ['1-10','11-50','51-200','201-500','500+'];
const HIRING_TIMELINES = ['1-2 weeks','2-4 weeks','1-2 months','2-3 months','3+ months'];
const COMMON_BENEFITS = ['Medical Aid','Pension Fund','Remote Work','Flexible Hours','Performance Bonus','13th Cheque','Study Assistance','Company Car','Gym Membership','Life Insurance'];
const COMMON_INDUSTRIES = ['Technology','Finance','Healthcare','Retail','Construction','Education','Manufacturing','Logistics','Legal','Marketing','Engineering','Hospitality'];

// ── Field — lifted OUTSIDE parent so it never remounts on re-render ──────────
function Field({ label, field, type = 'text', options, hint, editing, profile, editedProfile, setEditedProfile, onSave, onCancel, saving }) {
  const isEditing = editing === field;
  const val = profile[field];
  const displayVal = Array.isArray(val) ? val.join(', ') : val;

  return (
    <div className="form-group" style={{ marginBottom: '1.2rem' }}>
      <label className="form-label" style={{ display: 'block', marginBottom: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>
        {label}
      </label>
      {isEditing ? (
        <div className="rp-edit-row">
          {options ? (
            <select
              className="form-control"
              value={editedProfile[field] || ''}
              onChange={e => setEditedProfile(p => ({ ...p, [field]: e.target.value }))}
              autoFocus
            >
              <option value="">Select...</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : type === 'textarea' ? (
            <textarea
              className="form-control"
              rows="4"
              value={editedProfile[field] || ''}
              onChange={e => setEditedProfile(p => ({ ...p, [field]: e.target.value }))}
              autoFocus
            />
          ) : type === 'number' ? (
            <input
              className="form-control"
              type="number"
              value={editedProfile[field] || ''}
              onChange={e => setEditedProfile(p => ({ ...p, [field]: e.target.value ? parseInt(e.target.value) : null }))}
              autoFocus
            />
          ) : (
            <input
              className="form-control"
              type={type}
              value={editedProfile[field] || ''}
              onChange={e => setEditedProfile(p => ({ ...p, [field]: e.target.value }))}
              autoFocus
            />
          )}
          {hint && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>{hint}</p>}
          <div className="rp-edit-btns">
            <button className="btn btn-primary btn-sm" onClick={() => onSave(field)} disabled={saving}>
              {saving ? <i className="fas fa-spinner fa-spin"></i> : 'Save'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => onCancel(field)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="rp-view-row">
          <span className="rp-view-value" style={{ color: displayVal ? 'var(--text-primary)' : 'var(--text-muted)', fontStyle: displayVal ? 'normal' : 'italic' }}>
            {displayVal || 'Not specified'}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => onSave._setEditing(field)}><i className="fas fa-edit"></i></button>
        </div>
      )}
    </div>
  );
}

// ── TagField — lifted OUTSIDE parent ─────────────────────────────────────────
function TagField({ label, field, suggestions = [], editing, profile, editedProfile, setEditedProfile, onSave, onCancel, saving }) {
  const isEditing = editing === field;
  const tags = Array.isArray(profile[field]) ? profile[field] : [];
  const editTags = Array.isArray(editedProfile[field]) ? editedProfile[field] : [];
  const [input, setInput] = useState('');

  const addTag = (tag) => {
    const t = tag.trim();
    if (t && !editTags.includes(t)) setEditedProfile(p => ({ ...p, [field]: [...editTags, t] }));
    setInput('');
  };
  const removeTag = (t) => setEditedProfile(p => ({ ...p, [field]: editTags.filter(x => x !== t) }));

  return (
    <div className="form-group" style={{ marginBottom: '1.2rem' }}>
      <label className="form-label" style={{ display: 'block', marginBottom: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>
        {label}
      </label>
      {isEditing ? (
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
            {editTags.map(t => (
              <span key={t} style={{ background: 'var(--primary)', color: '#fff', borderRadius: '999px', padding: '3px 10px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                {t}
                <button onClick={() => removeTag(t)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input
              className="form-control"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(input); } }}
              placeholder="Type and press Enter"
              style={{ flex: 1 }}
              autoFocus
            />
            <button className="btn btn-secondary btn-sm" onClick={() => addTag(input)}>Add</button>
          </div>
          {suggestions.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.6rem' }}>
              {suggestions.filter(s => !editTags.includes(s)).map(s => (
                <button key={s} onClick={() => addTag(s)} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '999px', padding: '2px 10px', fontSize: '0.75rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  + {s}
                </button>
              ))}
            </div>
          )}
          <div className="rp-edit-btns">
            <button className="btn btn-primary btn-sm" onClick={() => onSave(field, editTags)} disabled={saving}>
              {saving ? <i className="fas fa-spinner fa-spin"></i> : 'Save'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => onCancel(field)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="rp-view-row">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', flex: 1 }}>
            {tags.length > 0
              ? tags.map(t => <span key={t} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '999px', padding: '3px 10px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t}</span>)
              : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not specified</span>
            }
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => onSave._setEditing(field)}><i className="fas fa-edit"></i></button>
        </div>
      )}
    </div>
  );
}

// ── ToggleField — lifted OUTSIDE parent ──────────────────────────────────────
function ToggleField({ label, field, hint, profile, onSave }) {
  const val = !!profile[field];
  return (
    <div className="form-group" style={{ marginBottom: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <label className="form-label" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>{label}</label>
        {hint && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>{hint}</p>}
      </div>
      <button
        onClick={() => onSave(field, !val)}
        style={{ width: 44, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', background: val ? 'var(--primary)' : 'var(--border)', position: 'relative', transition: 'background 0.2s' }}
      >
        <span style={{ position: 'absolute', top: 3, left: val ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function RecruiterProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const response = await recruiterAPI.getProfile();
      const data = response.data;
      if (data.company_logo && !data.company_logo.startsWith('http') && !data.company_logo.startsWith('data:')) {
        data.company_logo = `${MEDIA_URL}/${data.company_logo.replace(/^\/+/, '')}`;
      }
      setProfile(data);
      setEditedProfile(data);
      setLogoPreview(data.company_logo);
    } catch (err) {
      if (err.response?.status === 404) navigate('/recruiter/profile-setup');
      else if (err.response?.status === 401) navigate('/recruiter/login');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (field, value) => {
    setSaving(true);
    try {
      const payload = { [field]: value !== undefined ? value : editedProfile[field] };
      await recruiterAPI.updateProfile(payload);
      setProfile(prev => ({ ...prev, [field]: payload[field] }));
      setEditing(false);
      setSaveMsg('Saved ✓');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch {
      setSaveMsg('Failed to save');
      setTimeout(() => setSaveMsg(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Attach setEditing so Field's edit button can call it
  handleSave._setEditing = setEditing;

  const handleCancel = (field) => {
    setEditedProfile(p => ({ ...p, [field]: profile[field] }));
    setEditing(false);
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    try {
      const res = await recruiterAPI.uploadLogo(file);
      const raw = res.data.logo_url;
      const logoUrl = raw.startsWith('http') ? raw : `${MEDIA_URL}/${raw.replace(/^\/+/, '')}`;
      setLogoPreview(logoUrl);
      setProfile(prev => ({ ...prev, company_logo: logoUrl }));
    } catch { alert('Failed to upload logo'); }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const token = localStorage.getItem('token');
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch(`${API}/recruiter-profile/me/cover/`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await res.json();
      if (res.ok) setProfile(prev => ({ ...prev, cover_image: data.cover_url }));
      else alert(data.detail || 'Failed to upload cover');
    } catch { alert('Failed to upload cover image'); }
  };

  if (loading) return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main"><div className="portal-content"><div className="empty-state"><i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i><p>Loading profile...</p></div></div></main>
    </div>
  );

  if (!profile) return null;

  const isBusiness = ['business'].includes(profile.current_plan?.toLowerCase());

  // Shared props passed to every Field/TagField
  const fp = { editing, profile, editedProfile, setEditedProfile, onSave: handleSave, onCancel: handleCancel, saving };

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Company Profile</span>
          <div className="topbar-actions">
            {saveMsg && <span style={{ color: saveMsg.includes('Failed') ? '#ef4444' : '#10b981', fontSize: '0.85rem', fontWeight: 600 }}>{saveMsg}</span>}
          </div>
        </div>

        <div className="portal-content">

          {/* Header */}
          <div className="card rp-header">
            <div className="rp-logo-wrap">
              {logoPreview
                ? <img src={logoPreview} alt="Company Logo" className="rp-logo-img" />
                : <div className="rp-logo-placeholder"><i className="fas fa-building"></i></div>
              }
              <label className="rp-logo-upload" title="Upload logo">
                <i className="fas fa-camera"></i>
                <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
              </label>
            </div>
            <div className="rp-header-info">
              <h1 className="rp-company-name">{profile.company_name}</h1>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>{profile.industry || 'Industry not specified'}</p>
              <div className="rp-header-badges">
                <span className="badge badge-blue"><i className="fas fa-chart-pie"></i> {profile.profile_completion_percentage}% Complete</span>
                {profile.is_verified && <span className="badge badge-green"><i className="fas fa-check-circle"></i> Verified</span>}
                <span className="badge badge-purple"><i className="fas fa-crown"></i> {profile.current_plan} Plan</span>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><span className="card-title"><i className="fas fa-building" style={{ marginRight: '0.5rem', color: 'var(--primary)' }}></i>Company Information</span></div>
            <Field {...fp} label="Company Name" field="company_name" />
            <Field {...fp} label="Industry" field="industry" />
            <Field {...fp} label="Company Size" field="company_size" options={COMPANY_SIZES} />
            <Field {...fp} label="Founded Year" field="founded_year" type="number" hint="e.g. 2010" />
            <Field {...fp} label="Registration Number" field="registration_number" hint="CIPC registration number" />
            <Field {...fp} label="Website" field="company_website" type="url" />
          </div>

          {/* About Company */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><span className="card-title"><i className="fas fa-info-circle" style={{ marginRight: '0.5rem', color: 'var(--primary)' }}></i>About Company</span></div>
            <Field {...fp} label="Company Description" field="about_company" type="textarea" />
            <Field {...fp} label="Mission Statement" field="mission_statement" type="textarea" hint="What is your company's purpose?" />
            <Field {...fp} label="Company Culture" field="company_culture" type="textarea" hint="Describe your work environment and culture" />
            <TagField {...fp} label="Company Values" field="values" suggestions={['Innovation','Integrity','Teamwork','Excellence','Customer Focus','Diversity','Sustainability','Accountability']} />
          </div>

          {/* Contact & Location */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><span className="card-title"><i className="fas fa-address-card" style={{ marginRight: '0.5rem', color: 'var(--primary)' }}></i>Contact & Location</span></div>
            <Field {...fp} label="Contact Person" field="contact_person_name" />
            <Field {...fp} label="Contact Title" field="contact_person_title" hint="e.g. HR Manager, Talent Acquisition Lead" />
            <Field {...fp} label="Phone" field="phone" type="tel" />
            <Field {...fp} label="Street Address" field="street_address" />
            <Field {...fp} label="City" field="city" />
            <Field {...fp} label="Province" field="province" options={SA_PROVINCES} />
            <Field {...fp} label="Postal Code" field="postal_code" />
          </div>

          {/* Social Media */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><span className="card-title"><i className="fas fa-share-alt" style={{ marginRight: '0.5rem', color: 'var(--primary)' }}></i>Social Media</span></div>
            <Field {...fp} label="LinkedIn" field="linkedin_url" type="url" hint="https://linkedin.com/company/yourcompany" />
            <Field {...fp} label="Facebook" field="facebook_url" type="url" />
            <Field {...fp} label="Twitter / X" field="twitter_url" type="url" />
            <Field {...fp} label="Instagram" field="instagram_url" type="url" />
          </div>

          {/* Benefits & Perks */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><span className="card-title"><i className="fas fa-gift" style={{ marginRight: '0.5rem', color: 'var(--primary)' }}></i>Benefits & Perks</span></div>
            <TagField {...fp} label="Benefits" field="benefits" suggestions={COMMON_BENEFITS} />
          </div>

          {/* Hiring Information */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header"><span className="card-title"><i className="fas fa-users" style={{ marginRight: '0.5rem', color: 'var(--primary)' }}></i>Hiring Information</span></div>
            <Field {...fp} label="Typical Hiring Timeline" field="typical_hiring_timeline" options={HIRING_TIMELINES} />
            <Field {...fp} label="Interview Process" field="interview_process" type="textarea" hint="Describe your interview stages (e.g. phone screen → technical → final)" />
            <TagField {...fp} label="Industries Hiring For" field="industries_hiring_for" suggestions={COMMON_INDUSTRIES} />
          </div>

          {/* Branding (Business tier) */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header">
              <span className="card-title"><i className="fas fa-palette" style={{ marginRight: '0.5rem', color: 'var(--primary)' }}></i>Branding</span>
              {!isBusiness && <span className="badge badge-purple" style={{ marginLeft: '0.5rem' }}><i className="fas fa-lock"></i> Business Plan</span>}
            </div>
            {!isBusiness ? (
              <div style={{ padding: '1rem 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Custom branding is available on the <strong>Business plan</strong>.{' '}
                <button className="btn btn-primary btn-sm" style={{ marginLeft: '0.5rem' }} onClick={() => navigate('/pricing')}>Upgrade</button>
              </div>
            ) : (
              <>
                <Field {...fp} label="Company Tagline" field="tagline" hint="Short tagline shown on your public profile" />
                <ToggleField label="Animate Tagline" field="animate_tagline" hint="Word-by-word slide-in animation on your public profile" profile={profile} onSave={handleSave} />
                <Field {...fp} label="Brand Colour" field="brand_color" type="color" hint="Used for hero background accents on your public profile" />
                <div className="form-group" style={{ marginBottom: '1.2rem' }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>Cover Image</label>
                  {profile.cover_image && (
                    <img src={profile.cover_image.startsWith('http') ? profile.cover_image : `${MEDIA_URL}/${profile.cover_image.replace(/^\/+/, '')}`}
                      alt="Cover" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 10, marginBottom: '0.6rem' }} />
                  )}
                  <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                    <i className="fas fa-upload"></i> {profile.cover_image ? 'Change Cover' : 'Upload Cover'}
                    <input type="file" accept="image/*" onChange={handleCoverChange} style={{ display: 'none' }} />
                  </label>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>Recommended: 1200×400px, max 5MB</p>
                </div>
              </>
            )}
          </div>

        </div>
      </main>
    </div>
  );
}

export default RecruiterProfile;
